import React, { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import EventImage from "@/components/EventImage";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/price";
import { ShoppingBag, Plus, ChevronLeft, ShieldCheck } from "lucide-react";

const COLOR_SWATCH = { Black: "#1a1a1a", Natural: "#e8ddc7" };

const CAT_KEY = { apparel: "store_cat_apparel", drinkware: "store_cat_drinkware", goods: "store_cat_goods" };

export default function ProductDetail() {
  const { slug } = useParams();
  const { t } = useLang();
  const { add, setOpen } = useCart();

  const { data: p, isLoading, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: async () => (await api.get(`/store/products/${slug}`)).data,
    retry: false,
  });
  const { data: all = [] } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/store/products")).data });

  const colors = useMemo(() => [...new Set(((p && p.variants) || []).map((v) => v.color).filter(Boolean))], [p]);
  const hasVariants = !!(p && Array.isArray(p.variants) && p.variants.length > 0);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");

  const activeColor = color || colors[0] || "";
  const sizes = useMemo(() => ((p && p.variants) || []).filter((v) => !activeColor || v.color === activeColor).map((v) => v.size), [p, activeColor]);
  const image = (p && p.color_images && activeColor && p.color_images[activeColor]) || (p && p.image);
  const colorLabel = (c) => t(`color_${c.toLowerCase()}`) || c;

  const related = useMemo(
    () => (p ? all.filter((x) => x.slug !== p.slug && x.category === p.category).slice(0, 3) : []),
    [all, p]
  );

  if (isLoading) {
    return <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-16"><div className="h-[480px] bg-[#121418] border border-[#272A34] rounded-lg animate-pulse" /></div>;
  }

  if (isError || !p) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <Seo title={t("pd_not_found")} noindex canonicalPath={`/store/${slug}`} />
        <p className="text-stone-400 mb-6" data-testid="product-not-found">{t("pd_not_found")}</p>
        <Link to="/store" data-testid="product-back-link" className="inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300"><ChevronLeft className="w-4 h-4" /> {t("pd_back")}</Link>
      </div>
    );
  }

  const handleAdd = () => {
    if (hasVariants && ((colors.length && !activeColor) || !size)) { toast.error(t("size_select_prompt")); return; }
    const variant = hasVariants ? p.variants.find((v) => v.size === size && (!activeColor || v.color === activeColor)) : null;
    add(p, 1, variant ? { color: activeColor || null, size, printful_variant_id: variant.printful_variant_id, image } : null);
    toast.success(t("added_to_cart"));
    setOpen(true);
  };

  const jsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: p.name,
    description: p.description || undefined,
    image: p.image ? [p.image] : undefined,
    category: p.category ? t(CAT_KEY[p.category]) : undefined,
    brand: { "@type": "Brand", name: "swiss-pagan.ch" },
    offers: {
      "@type": "Offer",
      priceCurrency: (p.currency || "chf").toUpperCase(),
      price: (p.price_cents / 100).toFixed(2),
      availability: (p.stock || 0) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      url: `${typeof window !== "undefined" ? window.location.origin : ""}/store/${p.slug}`,
    },
  };

  const inStock = (p.stock || 0) > 0;

  return (
    <>
      <Seo title={p.name} description={p.description || t("store_subtitle")} image={p.image} canonicalPath={`/store/${p.slug}`} ogType="product" jsonLd={jsonLd} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12" data-testid={`product-detail-${p.slug}`}>
        <Link to="/store" data-testid="product-back-link" className="inline-flex items-center gap-1.5 text-sm text-stone-400 hover:text-amber-300 mb-6">
          <ChevronLeft className="w-4 h-4" /> {t("pd_back")}
        </Link>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image */}
          <div className="relative rounded-lg overflow-hidden border border-[#272A34] bg-[#121418]">
            <EventImage src={image} alt={p.name} imgClassName="w-full h-full object-cover aspect-square" />
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              {p.edition === "swiss-pagan" && <span data-testid="detail-edition-badge" className="px-2.5 py-1 rounded-full text-[9px] font-mono uppercase tracking-wider bg-amber-500/90 text-stone-950 font-bold backdrop-blur">{t("store_edition_badge")}</span>}
              {p.sabbat && <span className="px-2.5 py-1 rounded-full text-[9px] font-mono uppercase tracking-wider bg-indigo-600/90 text-white font-bold backdrop-blur">{t("store_limited_badge")}</span>}
            </div>
          </div>

          {/* Info */}
          <div className="flex flex-col">
            {p.category && (
              <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2">{t(CAT_KEY[p.category]) || p.category}</div>
            )}
            <h1 className="font-serif text-3xl sm:text-4xl text-amber-100 leading-tight mb-3">{p.name}</h1>
            <div className="font-serif text-3xl text-amber-400 mb-1" data-testid="detail-price">{formatPrice(p.price_cents, p.currency)}</div>
            <div className="text-sm mb-6">
              {inStock ? <span className="text-emerald-400" data-testid="detail-stock">● {t("pd_in_stock")}</span> : <span className="text-red-400" data-testid="detail-stock">● {t("out_of_stock")}</span>}
            </div>

            {p.description && (
              <div className="mb-6">
                <div className="text-[11px] font-mono uppercase tracking-wider text-stone-500 mb-2">{t("pd_description")}</div>
                <p className="text-stone-300 leading-relaxed">{p.description}</p>
              </div>
            )}

            {hasVariants && colors.length > 0 && (
              <div className="mb-5">
                <label className="block text-[11px] font-mono uppercase tracking-wider text-stone-500 mb-2">{t("color_label")}</label>
                <div className="flex items-center gap-2" data-testid="detail-color-options">
                  {colors.map((c) => (
                    <button key={c} type="button" data-testid={`detail-color-${c}`} onClick={() => { setColor(c); setSize(""); }} title={colorLabel(c)}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${activeColor === c ? "border-amber-500 scale-110" : "border-[#272A34] hover:border-stone-500"}`}
                      style={{ backgroundColor: COLOR_SWATCH[c] || "#888" }} />
                  ))}
                  <span className="text-sm text-stone-400 ml-1">{activeColor ? colorLabel(activeColor) : ""}</span>
                </div>
              </div>
            )}

            {hasVariants && (
              <div className="mb-6">
                <label className="block text-[11px] font-mono uppercase tracking-wider text-stone-500 mb-2">{t("size_label")}</label>
                <select data-testid="detail-size-select" value={size} onChange={(e) => setSize(e.target.value)} className="w-full sm:w-56 bg-[#181A20] border border-[#272A34] rounded-md px-3 py-2.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
                  <option value="">{t("size_select_prompt")}</option>
                  {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}

            {p.sabbat && <p className="text-xs text-indigo-300/80 mb-4">{t("pd_seasonal_note")}</p>}

            <button data-testid="detail-add-to-cart" onClick={handleAdd} disabled={!inStock}
              className="inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-6 py-3.5 rounded-md text-sm transition-colors disabled:opacity-50 w-full sm:w-auto">
              <Plus className="w-4 h-4" /> {inStock ? t("add_to_cart") : t("out_of_stock")}
            </button>

            <div className="flex items-center gap-2 text-xs text-stone-500 mt-4">
              <ShieldCheck className="w-4 h-4 text-amber-600/70" /> {t("pd_shipping_note")}
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-16">
            <h2 className="font-serif text-2xl text-amber-100 mb-6 flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-amber-500" /> {t("pd_related")}</h2>
            <div className="grid sm:grid-cols-3 gap-6" data-testid="detail-related">
              {related.map((r) => (
                <Link key={r.slug} to={`/store/${r.slug}`} data-testid={`related-${r.slug}`} className="group bg-[#121418] border border-[#272A34] hover:border-amber-500/50 rounded-lg overflow-hidden transition-all hover:-translate-y-1">
                  <div className="h-48 overflow-hidden">
                    <EventImage src={r.image} alt={r.name} imgClassName="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-serif text-base text-stone-100 leading-snug mb-1 group-hover:text-amber-100 line-clamp-1">{r.name}</h3>
                    <span className="font-serif text-lg text-amber-400">{formatPrice(r.price_cents, r.currency)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
